import { useCallback, useEffect, useMemo, useState } from "react"

import { Subscription } from "@rails/actioncable"

import EventEmitter from "src/EventEmitter"

import { RosterEntry } from "./useRoster"

type useStudentControlsManagementProps = {
  initialControlsUnlockedUserIds?: string[]
  channel?: Subscription
  roster: Record<string, RosterEntry>
  localAttendeeId: string
}

const useStudentControlsManagement = ({
  initialControlsUnlockedUserIds = [],
  channel,
  roster,
  localAttendeeId,
}: useStudentControlsManagementProps) => {
  const [controlsUnlockedUserIds, setControlsUnlockedUserIds] = useState(
    initialControlsUnlockedUserIds
  )

  useEffect(() => {
    const updateIds = (data: { controlsUnlockedUserIds?: string[] }) => {
      if (data.controlsUnlockedUserIds) {
        setControlsUnlockedUserIds(data.controlsUnlockedUserIds)
      }
    }

    EventEmitter.subscribe("mute_all", updateIds)
    EventEmitter.subscribe("lock_all", updateIds)
    EventEmitter.subscribe("unlock_all", updateIds)
    EventEmitter.subscribe("student_controls_updated", updateIds)
    EventEmitter.subscribe("user_joined_or_updated", updateIds)

    return () => {
      EventEmitter.unsubscribe("mute_all", updateIds)
      EventEmitter.unsubscribe("lock_all", updateIds)
      EventEmitter.unsubscribe("unlock_all", updateIds)
      EventEmitter.unsubscribe("student_controls_updated", updateIds)
      EventEmitter.unsubscribe("user_joined_or_updated", updateIds)
    }
  }, [])

  const presentStudents = useMemo(
    () =>
      Object.values(roster).filter(
        entry =>
          entry.userType === "Student" && entry.attendeeId !== localAttendeeId
      ),
    [roster, localAttendeeId]
  )
  const presentStudentIds = presentStudents
    .map(entry => entry.userId)
    .filter((id): id is string => Boolean(id))
  const hasPresentStudents = presentStudents.length > 0

  const allControlsLocked =
    presentStudentIds.length > 0 &&
    presentStudentIds.every(id => !controlsUnlockedUserIds.includes(id))
  const allStudentsMuted =
    hasPresentStudents && presentStudents.every(user => user.muted)

  const toggleAllControlsLocked = useCallback(() => {
    if (!channel) return

    if (allControlsLocked) {
      channel.perform("unlock_all", {})
    } else {
      channel.perform("lock_all", {})
    }
  }, [allControlsLocked, channel])

  const muteAll = useCallback(() => {
    if (!channel) return

    channel.perform("mute_all", {})
  }, [channel])

  return {
    controlsUnlockedUserIds,
    hasPresentStudents,
    allControlsLocked,
    toggleAllControlsLocked,
    allStudentsMuted,
    muteAll,
  }
}

export default useStudentControlsManagement
